// Cross-portal switcher footer.
// Shown on login pages and at the bottom of every authenticated portal layout
// so users who land on the wrong portal can self-serve their way to the right
// one. The "current" portal is shown but rendered as inactive.
import { User, Briefcase, Terminal } from "lucide-react";

type Portal = "investor" | "banker" | "admin";

const ITEMS: { key: Portal; label: string; href: string; icon: typeof User }[] = [
  { key: "investor", label: "Investor Portal", href: "/investor/login", icon: User },
  { key: "banker", label: "Banker Panel", href: "/banker/login", icon: Briefcase },
  { key: "admin", label: "Admin Console", href: "/admin/login", icon: Terminal },
];

export function PortalSwitcher({
  current,
  tone = "dark",
  className = "",
}: {
  current: Portal;
  tone?: "dark" | "light";
  className?: string;
}) {
  const isDark = tone === "dark";
  const baseText = isDark ? "text-white/40" : "text-muted-foreground";
  const hoverText = isDark ? "hover:text-white/80" : "hover:text-foreground";
  const activeText = isDark ? "text-white/70" : "text-foreground";
  const divider = isDark ? "text-white/15" : "text-muted-foreground/40";

  return (
    <div
      className={`flex items-center justify-center gap-1 flex-wrap text-[11px] ${className}`}
      aria-label="Switch portal"
    >
      {ITEMS.map((item, i) => {
        const Icon = item.icon;
        const isCurrent = item.key === current;
        const inner = (
          <span className="inline-flex items-center gap-1.5 px-2 py-1">
            <Icon className="w-3 h-3" />
            {item.label}
            {isCurrent && (
              <span
                className={`ml-1 text-[9px] uppercase tracking-wider ${baseText}`}
              >
                · you're here
              </span>
            )}
          </span>
        );
        return (
          <span key={item.key} className="inline-flex items-center">
            {isCurrent ? (
              <span className={`${activeText}`}>{inner}</span>
            ) : (
              <a
                href={item.href}
                className={`${baseText} ${hoverText} transition-colors rounded`}
              >
                {inner}
              </a>
            )}
            {i < ITEMS.length - 1 && (
              <span className={divider} aria-hidden>
                ·
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
